import { useState } from "react";
import type { ApiJob } from "@tradingdroid/frontends-types";

import { useJobPolling, useJobs } from "@src/hooks/redux";
import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage } from "@src/utils/apiError";
import { isWorkerStillStoppingError } from "@src/utils/jobPauseResume";

export const WORKER_STILL_STOPPING_MESSAGE =
  "The worker is still stopping this optimization. Resume it once its current trials have finished.";

/**
 * Pause and resume handlers for optimization jobs. Each refreshes the job in the store and hands the
 * fresh row to `onUpdated`, so a page holding its own copy can replace it.
 */
export const useJobPauseResume = (onUpdated?: (job: ApiJob) => void) => {
  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();
  const { upsertJob } = useJobs();
  const { trackActiveJob, untrackActiveJob } = useJobPolling();
  const [busyJobId, setBusyJobId] = useState<number | null>(null);

  const applyFreshJob = (fresh: ApiJob | undefined) => {
    if (!fresh) return;
    upsertJob(fresh);
    onUpdated?.(fresh);
  };

  // A refused action leaves the screen on a row the server has moved past, so reload it either way.
  const reloadJob = async (jobId: number) => {
    try {
      applyFreshJob(await tdApi.getJob(jobId));
    } catch {
      // The toast already reported the action's outcome; a failed reload only leaves the row stale.
    }
  };

  const pauseJob = async (job: ApiJob) => {
    const { confirmed } = await confirmModal(
      `Pause optimization #${job.id}? The worker stops after its current trial, and a resume continues the same study.`,
      { title: "Pause optimization", confirmLabel: "Pause", confirmColor: "warning" },
    );
    if (!confirmed) return;

    setBusyJobId(job.id);
    try {
      await tdApi.patchJob(job.id, { status_name: "paused" });
      // This toast reports the pause, so the poller must not announce it a second time.
      untrackActiveJob(job.id);
      showToast({ message: `Optimization #${job.id} paused`, type: "info" });
    } catch (err) {
      showToast({
        title: `Could not pause job #${job.id}`,
        message: getApiErrorMessage(err),
        type: "danger",
      });
    } finally {
      await reloadJob(job.id);
      setBusyJobId(null);
    }
  };

  const resumeJob = async (job: ApiJob) => {
    setBusyJobId(job.id);
    try {
      applyFreshJob(await tdApi.resumeJob(job.id));
      // Tracked by id rather than left to discovery, which reads only one page of active jobs.
      trackActiveJob({
        jobId: job.id,
        jobType: "optimization",
        entityType: "optimization",
        metadata: {
          title: `optimization Job ${job.id}`,
          description: "Resumed from the Workstation",
        },
      });
      showToast({ message: `Optimization #${job.id} resumed`, type: "success" });
    } catch (err) {
      if (isWorkerStillStoppingError(err)) {
        showToast({ message: WORKER_STILL_STOPPING_MESSAGE, type: "warning" });
      } else {
        showToast({
          title: `Could not resume job #${job.id}`,
          message: getApiErrorMessage(err),
          type: "danger",
        });
        await reloadJob(job.id);
      }
    } finally {
      setBusyJobId(null);
    }
  };

  return { pauseJob, resumeJob, busyJobId };
};
